// contrastColor.ts
// Shared colour-contrast helpers. Same luminance/HSL math the Logo wordmark
// uses, factored out so the top-bar nav (and anything else) can tune a themed
// colour against a background identically. Suggested home: src/utils/contrastColor.ts

export const parseRgb = (color?: string): { r: number; g: number; b: number } | null => {
    if (!color) return null;
    let c = color.trim().toLowerCase();
    if (c === "black") return { r: 0, g: 0, b: 0 };
    if (c === "white") return { r: 1, g: 1, b: 1 };

    const rgbMatch = c.match(/^rgba?\(([^)]+)\)/);
    if (rgbMatch) {
        const parts = rgbMatch[1].split(",").map((s) => parseFloat(s.trim()));
        if (parts.length >= 3 && parts.slice(0, 3).every((n) => !isNaN(n))) {
            return { r: parts[0] / 255, g: parts[1] / 255, b: parts[2] / 255 };
        }
        return null;
    }

    let hex = c.replace("#", "");
    if (hex.length === 3) hex = hex.split("").map((ch) => ch + ch).join("");
    if (hex.length === 8) hex = hex.substring(0, 6);
    if (hex.length !== 6) return null;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some(isNaN)) return null;
    return { r: r / 255, g: g / 255, b: b / 255 };
};

export const luminance = (c: { r: number; g: number; b: number }): number => {
    const toLinear = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    return 0.2126 * toLinear(c.r) + 0.7152 * toLinear(c.g) + 0.0722 * toLinear(c.b);
};

export const contrastRatio = (a?: string, b?: string): number | null => {
    const ca = parseRgb(a);
    const cb = parseRgb(b);
    if (!ca || !cb) return null;
    const la = luminance(ca);
    const lb = luminance(cb);
    const hi = Math.max(la, lb);
    const lo = Math.min(la, lb);
    return (hi + 0.05) / (lo + 0.05);
};

// black or white — whichever contrasts the background more
export const bestBlackOrWhite = (bg?: string): string => {
    const c = parseRgb(bg);
    if (!c) return "#000000";
    const L = luminance(c);
    const withWhite = 1.05 / (L + 0.05);
    const withBlack = (L + 0.05) / 0.05;
    return withBlack >= withWhite ? "#000000" : "#ffffff";
};

const rgbToHsl = (r: number, g: number, b: number) => {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h, s, l };
};

const hslToRgb = (h: number, s: number, l: number) => {
    if (s === 0) return { r: l, g: l, b: l };
    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return {
        r: hue2rgb(p, q, h + 1 / 3),
        g: hue2rgb(p, q, h),
        b: hue2rgb(p, q, h - 1 / 3),
    };
};

const rgbToHex = (c: { r: number; g: number; b: number }): string => {
    const to255 = (v: number) =>
        Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, "0");
    return `#${to255(c.r)}${to255(c.g)}${to255(c.b)}`;
};

// Keep the colour's hue/saturation, shift only lightness until it clears the
// contrast threshold against the background. Degrades to ~black/white at the
// extreme (which always passes), but stops early if a tint already works.
export const tuneForContrast = (color: string, bg: string, threshold: number): string => {
    const colorRgb = parseRgb(color);
    const bgRgb = parseRgb(bg);
    if (!colorRgb || !bgRgb) return bestBlackOrWhite(bg);

    const bgLum = luminance(bgRgb);
    // pick the direction with more contrast headroom
    const darken = (bgLum + 0.05) / 0.05 >= 1.05 / (bgLum + 0.05);

    const { h, s } = rgbToHsl(colorRgb.r, colorRgb.g, colorRgb.b);
    let l = rgbToHsl(colorRgb.r, colorRgb.g, colorRgb.b).l;
    const step = 0.02;

    for (let i = 0; i <= 60; i++) {
        const candidate = hslToRgb(h, s, l);
        const cl = luminance(candidate);
        const hi = Math.max(cl, bgLum);
        const lo = Math.min(cl, bgLum);
        if ((hi + 0.05) / (lo + 0.05) >= threshold) break;
        l = darken ? Math.max(0, l - step) : Math.min(1, l + step);
    }

    return rgbToHex(hslToRgb(h, s, l));
};

// High-level helper: return `preferred` if it already clears `threshold` against
// `bg`; otherwise a tuned version that does. Falls back to black/white only when
// `preferred` itself is unusable, and leaves `preferred` untouched when `bg`
// can't be measured (so a colour is never "fixed" on a guess).
export const ensureContrast = (preferred: string, bg: string, threshold = 3): string => {
    const pre = parseRgb(preferred);
    if (!pre) return bestBlackOrWhite(bg);
    const bgc = parseRgb(bg);
    if (!bgc) return preferred;
    const ratio = contrastRatio(preferred, bg) as number;
    return ratio >= threshold ? preferred : tuneForContrast(preferred, bg, threshold);
};